import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import '../styles/HavaDurumu.css';
import { Container } from 'react-bootstrap';

interface MGMWeatherData {
  il: string;
  ilce: string;
  tarih: string;
  sicaklik: number;
  hissedilenSicaklik: number;
  nem: number;
  ruzgarHizi: number;
  ruzgarYonu: number;
  basinc: number;
  hadise: string;
  gunDogumu: string;
  gunBatimi: string;
}

interface MGMForecastData {
  tarih: string;
  gun: string;
  sicaklik: number;
  hadise: string;
  icon: string;
}

const HavaDurumu: React.FC = () => {
  const navigate = useNavigate();
  const [weatherData, setWeatherData] = useState<MGMWeatherData | null>(null);
  const [forecastData, setForecastData] = useState<MGMForecastData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchWeatherData = async () => {
      setLoading(true);
      try {
        // MGM'den veri çekme simülasyonu
        // Gerçek uygulamada CORS sorunları nedeniyle bir proxy server üzerinden istek yapılmalıdır
        
        // Güncel hava durumu verisi
        const currentWeatherData: MGMWeatherData = {
          il: "İstanbul",
          ilce: "Arnavutköy",
          tarih: new Date().toISOString(),
          sicaklik: 18,
          hissedilenSicaklik: 16,
          nem: 65,
          ruzgarHizi: 15,
          ruzgarYonu: 180,
          basinc: 1015,
          hadise: "Parçalı Bulutlu",
          gunDogumu: "06:30",
          gunBatimi: "19:45"
        };
        
        setWeatherData(currentWeatherData);
        
        // 5 günlük tahmin verisi
        const forecastData: MGMForecastData[] = [
          {
            tarih: new Date(Date.now() + 86400000).toISOString(),
            gun: "Pazartesi",
            sicaklik: 19,
            hadise: "Parçalı Bulutlu",
            icon: "partly_cloudy"
          },
          {
            tarih: new Date(Date.now() + 2 * 86400000).toISOString(),
            gun: "Salı",
            sicaklik: 20,
            hadise: "Açık",
            icon: "clear"
          },
          {
            tarih: new Date(Date.now() + 3 * 86400000).toISOString(),
            gun: "Çarşamba",
            sicaklik: 17,
            hadise: "Hafif Yağmurlu",
            icon: "light_rain"
          },
          {
            tarih: new Date(Date.now() + 4 * 86400000).toISOString(),
            gun: "Perşembe",
            sicaklik: 16,
            hadise: "Yağmurlu",
            icon: "rain"
          },
          {
            tarih: new Date(Date.now() + 5 * 86400000).toISOString(),
            gun: "Cuma",
            sicaklik: 18,
            hadise: "Parçalı Bulutlu",
            icon: "partly_cloudy"
          }
        ];
        
        setForecastData(forecastData);
        setError(null);
      } catch (err) {
        console.error('Hava durumu verisi alma hatası:', err);
        setError('Hava durumu verileri yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWeatherData();
    
    // 30 dakikada bir güncelle
    const interval = setInterval(fetchWeatherData, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Geri dön
  const handleBack = () => {
    navigate(-1);
  };
  
  // Hava durumu ikonunu al
  const getWeatherIcon = (hadise: string) => {
    const iconMap: { [key: string]: string } = {
      'Açık': 'fas fa-sun',
      'Az Bulutlu': 'fas fa-cloud-sun',
      'Parçalı Bulutlu': 'fas fa-cloud-sun',
      'Çok Bulutlu': 'fas fa-cloud',
      'Kapalı': 'fas fa-cloud',
      'Sisli': 'fas fa-smog',
      'Hafif Yağmurlu': 'fas fa-cloud-rain',
      'Yağmurlu': 'fas fa-cloud-showers-heavy',
      'Kuvvetli Yağmurlu': 'fas fa-cloud-showers-heavy',
      'Karla Karışık Yağmurlu': 'fas fa-cloud-meatball',
      'Kar Yağışlı': 'fas fa-snowflake',
      'Dolu': 'fas fa-cloud-meatball',
      'Sağanak Yağışlı': 'fas fa-cloud-showers-heavy',
      'Gökgürültülü Sağanak Yağışlı': 'fas fa-bolt',
      'Kuvvetli Gökgürültülü Sağanak Yağışlı': 'fas fa-bolt'
    };
    
    return iconMap[hadise] || 'fas fa-cloud';
  };
  
  // Rüzgar yönünü hesapla
  const getWindDirection = (degrees: number): string => {
    const directions = ['Kuzey', 'Kuzeydoğu', 'Doğu', 'Güneydoğu', 'Güney', 'Güneybatı', 'Batı', 'Kuzeybatı'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };
  
  return (
    <div className="havadurumu-container" style={{ paddingTop: 70 }}>
      <Header title="Hava Durumu" />
      <Container className="py-4">
        <main className="hava-main">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Hava durumu bilgileri yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <p>{error}</p>
              <button className="retry-button" onClick={() => window.location.reload()}>
                Tekrar Dene
              </button>
            </div>
          ) : weatherData ? (
            <>
              <div className="weather-card">
                <div className="weather-header">
                  <div className="weather-location">
                    <h2>{weatherData.il}</h2>
                    <p className="current-date">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
                
                <div className="weather-body">
                  <div className="weather-icon-temp">
                    <div className="weather-icon-large">
                      <i className={getWeatherIcon(weatherData.hadise)}></i>
                    </div>
                    <div className="temperature">
                      <span className="temp-value">{Math.round(weatherData.sicaklik)}</span>
                      <span className="temp-unit">°C</span>
                    </div>
                  </div>
                  
                  <div className="weather-description">
                    <h3>{weatherData.hadise}</h3>
                    <p>Hissedilen: {Math.round(weatherData.hissedilenSicaklik)}°C</p>
                  </div>
                </div>
                
                <div className="weather-details">
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-wind"></i>
                    </div>
                    <div className="detail-info">
                      <span className="detail-label">Rüzgar</span>
                      <span className="detail-value">{Math.round(weatherData.ruzgarHizi)} km/sa</span>
                      <span className="detail-subvalue">{getWindDirection(weatherData.ruzgarYonu)}</span>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-tint"></i>
                    </div>
                    <div className="detail-info">
                      <span className="detail-label">Nem</span>
                      <span className="detail-value">{weatherData.nem}%</span>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-compress-alt"></i>
                    </div>
                    <div className="detail-info">
                      <span className="detail-label">Basınç</span>
                      <span className="detail-value">{weatherData.basinc} hPa</span>
                    </div>
                  </div>
                </div>
                
                <div className="sun-times">
                  <div className="sun-item">
                    <div className="sun-icon">
                      <i className="fas fa-sun"></i>
                    </div>
                    <div className="sun-info">
                      <span className="sun-label">Gün Doğumu</span>
                      <span className="sun-time">{weatherData.gunDogumu}</span>
                    </div>
                  </div>
                  
                  <div className="sun-item">
                    <div className="sun-icon">
                      <i className="fas fa-moon"></i>
                    </div>
                    <div className="sun-info">
                      <span className="sun-label">Gün Batımı</span>
                      <span className="sun-time">{weatherData.gunBatimi}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {forecastData.length > 0 && (
                <div className="weather-forecast-card">
                  <h3><i className="fas fa-calendar-alt"></i> 5 Günlük Tahmin</h3>
                  
                  <div className="forecast-items">
                    {forecastData.map((day, index) => (
                      <div key={index} className="forecast-item">
                        <div className="forecast-day">{day.gun}</div>
                        <div className="forecast-icon">
                          <i className={getWeatherIcon(day.hadise)}></i>
                        </div>
                        <div className="forecast-temp">
                          <span className="forecast-temp-value">{Math.round(day.sicaklik)}°C</span>
                        </div>
                        <div className="forecast-desc">
                          {day.hadise}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <p className="forecast-note">
                    <i className="fas fa-info-circle"></i> Detaylı tahmin bilgileri için <a href="https://www.mgm.gov.tr/tahmin/il-ve-ilceler.aspx?il=%C4%B0stanbul" target="_blank" rel="noopener noreferrer">Meteoroloji Genel Müdürlüğü</a>'nün resmi kaynaklarını kontrol ediniz.
                  </p>
                </div>
              )}
            </>
          ) : null}
        </main>
        
        <footer className="hava-footer">
          <p>Veriler Meteoroloji Genel Müdürlüğü'nden alınmaktadır. Son güncelleme: {new Date().toLocaleTimeString('tr-TR')}</p>
        </footer>
      </Container>
      
      {/* Tab Bar Bileşeni */}
      <TabBar />
    </div>
  );
};

export default HavaDurumu; 